import { get } from '../../api/commonAPI';
import { BOOKING } from '../../api/apiPaths';
import { message } from 'antd';
import * as actionTypes from './auditLogActionTypes';

/**
 * Fetch audit logs for a specific booking by ID
 * @param {string|number} bookingId - The booking ID
 */
export const fetchAuditLogsByBookingId = (bookingId) => {
  return (dispatch) => {
    dispatch({ type: actionTypes.FETCH_AUDIT_LOGS });

    // Remove trailing slash from BOOKING constant if present
    const bookingPath = BOOKING.endsWith('/') ? BOOKING.slice(0, -1) : BOOKING;
    const url = `${bookingPath}/audit/booking/${bookingId}`;
    
    get(url)
      .then((response) => {
        if (response && response.status >= 200 && response.status < 300) {
          const auditLogs = response.data?.data || response.data || [];
          
          if (auditLogs.length === 0) {
            message.info('No audit history found for this booking');
          }
          
          dispatch({
            type: actionTypes.FETCH_AUDIT_LOGS_SUCCESS,
            payload: auditLogs
          });
        } else if (response && response.status === 404) {
          message.info('No audit history found for this booking');
          dispatch({
            type: actionTypes.FETCH_AUDIT_LOGS_SUCCESS,
            payload: []
          });
        } else {
          dispatch({
            type: actionTypes.FETCH_AUDIT_LOGS_FAIL,
            payload: 'Failed to fetch audit logs'
          });
          message.error('Failed to load audit history');
        }
      })
      .catch((error) => {
        console.error('Error fetching audit logs:', error);
        const errorMessage = error.response?.data?.message || error.message || 'Failed to load audit history';
        
        dispatch({
          type: actionTypes.FETCH_AUDIT_LOGS_FAIL,
          payload: errorMessage
        });
        
        if (error.response?.status !== 404) {
          message.error(errorMessage);
        }
      });
  };
};

/**
 * Fetch audit logs by booking type
 * @param {string} bookingType - The booking type (OFFLINE_BOOKING, SYSTEM_BOOKING, RESERVATION)
 */
export const fetchAuditLogsByType = (bookingType) => {
  return (dispatch) => {
    dispatch({ type: actionTypes.FETCH_AUDIT_LOGS_BY_TYPE });

    // Remove trailing slash from BOOKING constant if present
    const bookingPath = BOOKING.endsWith('/') ? BOOKING.slice(0, -1) : BOOKING;
    const url = `${bookingPath}/audit/byType/${bookingType}`;
    
    get(url)
      .then((response) => {
        if (response && response.status >= 200 && response.status < 300) {
          const auditLogs = response.data?.data || response.data || [];
          
          dispatch({
            type: actionTypes.FETCH_AUDIT_LOGS_BY_TYPE_SUCCESS,
            payload: auditLogs
          });
        } else {
          dispatch({
            type: actionTypes.FETCH_AUDIT_LOGS_BY_TYPE_FAIL,
            payload: 'Failed to fetch audit logs by type'
          });
          message.error('Failed to load audit history by type');
        }
      })
      .catch((error) => {
        console.error('Error fetching audit logs by type:', error);
        const errorMessage = error.response?.data?.message || error.message || 'Failed to load audit history';
        
        dispatch({
          type: actionTypes.FETCH_AUDIT_LOGS_BY_TYPE_FAIL,
          payload: errorMessage
        });
        message.error(errorMessage);
      });
  };
};

/**
 * Fetch audit logs by date range
 * @param {string} startDate - Start date in ISO format
 * @param {string} endDate - End date in ISO format
 * @param {string} bookingType - Optional booking type filter
 */
export const fetchAuditLogsByDateRange = (startDate, endDate, bookingType = null) => {
  return (dispatch) => {
    dispatch({ type: actionTypes.FETCH_AUDIT_LOGS_BY_DATE_RANGE });

    // Remove trailing slash from BOOKING constant if present
    const bookingPath = BOOKING.endsWith('/') ? BOOKING.slice(0, -1) : BOOKING;
    let url = `${bookingPath}/audit/byDateRange?startDate=${startDate}&endDate=${endDate}`;
    
    if (bookingType) {
      url = `${bookingPath}/audit/byTypeAndDateRange?bookingType=${bookingType}&startDate=${startDate}&endDate=${endDate}`;
    }
    
    get(url)
      .then((response) => {
        if (response && response.status >= 200 && response.status < 300) {
          const auditLogs = response.data?.data || response.data || [];
          
          dispatch({
            type: actionTypes.FETCH_AUDIT_LOGS_BY_DATE_RANGE_SUCCESS,
            payload: auditLogs
          });
        } else {
          dispatch({
            type: actionTypes.FETCH_AUDIT_LOGS_BY_DATE_RANGE_FAIL,
            payload: 'Failed to fetch audit logs by date range'
          });
          message.error('Failed to load audit history by date range');
        }
      })
      .catch((error) => {
        console.error('Error fetching audit logs by date range:', error);
        const errorMessage = error.response?.data?.message || error.message || 'Failed to load audit history';
        
        dispatch({
          type: actionTypes.FETCH_AUDIT_LOGS_BY_DATE_RANGE_FAIL,
          payload: errorMessage
        });
        message.error(errorMessage);
      });
  };
};

/**
 * Clear audit logs from state
 */
export const clearAuditLogs = () => {
  return {
    type: actionTypes.CLEAR_AUDIT_LOGS
  };
};

/**
 * Clear audit log errors
 */
export const clearAuditLogError = () => {
  return {
    type: actionTypes.CLEAR_AUDIT_LOG_ERROR
  };
};
